// THE LINE-ITEM TABLES' COLUMN WIDTHS, AS DATA (QA 2026-09-30, C1-01 and C2-02).
//
// Both line-item tables use `table-layout: fixed`: the sized columns (the select
// box, #, the amounts, the icons) take their stated width, and the unsized text
// columns (Description, and Account / Department / Code / Company where they
// have no width) split whatever is left. The table's `min-width` is the only
// thing that stops the text columns being crushed; below it, the table scrolls
// inside its own wrapper instead.
//
// The two floors were hand-added sums, and both were wrong by the same two
// things. Fluent's `TableHeaderCell` pads 8px each side, and the cell is
// `box-sizing: content-box` by the browser's default for a table cell
// (Fluent sets `content-box` only on the cell's inner button), so the padding
// sits outside the stated width and every column is 16px wider than it, and
// D-228's select column arrived without the floor moving. At 953px the QA
// tester measured Description at 14px in the editor, and Description, Account
// and Department at 43px each in the read-only table. The floor is now worked
// out here, from the same widths the headers render, so adding a column moves
// it.

/** Fluent's small `TableHeaderCell` pads 8px each side; the browser's default
    `content-box` puts that padding outside its width. */
export const CELL_PADDING_X = 16

/** The narrowest a text column may be drawn, before its padding. */
export const TEXT_COLUMN_FLOOR = {
  description: 180,
  coding: 140,
} as const

/** The stated widths of the read-only `Line items` table's sized columns. */
export const READ_ONLY_WIDTHS = {
  select: 36,
  number: 40,
  price: 100,
  quantity: 90,
  tax: 84,
  shipping: 90,
  total: 100,
  details: 68,
  /** The icon column where lines can be checked: room for a third icon, the
      bulk row's Unselect All beside Additional Detail and Associated
      Entities (D-242). */
  detailsSelectable: 96,
} as const

/** The read-only table's icon column: wider where lines can be checked (D-242). */
export function readOnlyDetailsWidth(select: boolean): number {
  return select ? READ_ONLY_WIDTHS.detailsSelectable : READ_ONLY_WIDTHS.details
}

/** The stated widths of the Import Control editor's sized columns. Account and
    Department are sized here, so only Description flexes. */
export const EDITOR_WIDTHS = {
  select: 36,
  number: 30,
  account: 190,
  department: 170,
  price: 96,
  quantity: 76,
  tax: 84,
  shipping: 90,
  total: 104,
  actions: 96,
} as const

/** A fixed-layout table's floor: every sized column plus every text column's
    floor, each with its padding. */
export function fixedTableMinWidth(sized: readonly number[], textFloors: readonly number[]): number {
  const pad = (w: number) => w + CELL_PADDING_X
  return [...sized, ...textFloors].reduce((sum, w) => sum + pad(w), 0)
}

interface ReadOnlyColumns {
  select: boolean
  code: boolean
  account: boolean
  department: boolean
  distributing: boolean
}

/** The read-only table's floor for the columns it is showing. */
export function readOnlyLineTableMinWidth(c: ReadOnlyColumns): number {
  const w = READ_ONLY_WIDTHS
  const sized = [
    ...(c.select ? [w.select] : []),
    w.number,
    w.price,
    w.quantity,
    ...(c.distributing ? [w.tax, w.shipping] : []),
    w.total,
    readOnlyDetailsWidth(c.select),
  ]
  const text = [
    TEXT_COLUMN_FLOOR.description,
    ...(c.code ? [TEXT_COLUMN_FLOOR.coding] : []),
    ...(c.account ? [TEXT_COLUMN_FLOOR.coding] : []),
    ...(c.department ? [TEXT_COLUMN_FLOOR.coding] : []),
  ]
  return fixedTableMinWidth(sized, text)
}

interface EditorColumns {
  select: boolean
  distributing: boolean
}

/** The editor's floor for the columns it is showing. */
export function editorLineTableMinWidth(c: EditorColumns): number {
  const w = EDITOR_WIDTHS
  const sized = [
    ...(c.select ? [w.select] : []),
    w.number,
    w.account,
    w.department,
    w.price,
    w.quantity,
    ...(c.distributing ? [w.tax, w.shipping] : []),
    w.total,
    w.actions,
  ]
  return fixedTableMinWidth(sized, [TEXT_COLUMN_FLOOR.description])
}
